// Public car detail page with seller CTA and finance/insurance lead form.
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Phone, MessageCircle, ArrowLeft, Calendar, Gauge, Fuel, Cog, Palette,
  MapPin, Users, ShieldCheck, ChevronLeft, ChevronRight, X, BadgeCheck,
  FileCheck2, Award,
} from 'lucide-react';
import EmiCalculator from '../components/marketplace/EmiCalculator';
import { trackFinanceEvent } from '../lib/financeAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUEL_LABEL = { petrol: 'Petrol', diesel: 'Diesel', cng: 'CNG', electric: 'Electric', hybrid: 'Hybrid' };
const TRANS_LABEL = { manual: 'Manual', automatic: 'Automatic', amt: 'AMT', cvt: 'CVT', dct: 'DCT' };
const BODY_LABEL = {
  hatchback: 'Hatchback', sedan: 'Sedan', suv: 'SUV', muv: 'MUV',
  coupe: 'Coupé', convertible: 'Convertible', pickup: 'Pickup',
  van: 'Van', wagon: 'Wagon',
};

const inr = (n) => (n || n === 0) ? `₹${n.toLocaleString('en-IN')}` : '—';
const km = (n) => (n || n === 0) ? `${n.toLocaleString('en-IN')} km` : '—';

const waLink = (phone, text) => {
  const num = (phone || '').replace(/[^\d]/g, '');
  return `https://wa.me/${num}?text=${encodeURIComponent(text || '')}`;
};

const MarketplaceCar = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [leadModal, setLeadModal] = useState(null); // 'finance' | 'insurance' | 'trade_in' | null

  const fetchCar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/marketplace/cars/${id}`);
      setCar(data);
    } catch (e) {
      console.error('Car fetch failed', e);
      setCar(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCar(); }, [fetchCar]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto py-12 animate-pulse">
          <div className="h-64 bg-slate-200 rounded-2xl" />
          <div className="h-4 bg-slate-200 rounded w-1/3 mt-6" />
          <div className="h-8 bg-slate-200 rounded w-1/2 mt-3" />
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen bg-slate-50 pt-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto py-24 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Car not found</h1>
          <p className="text-slate-500 mt-2">This listing may have been sold or removed.</p>
          <Link to="/marketplace" className="mt-6 inline-flex items-center gap-1 text-teal-700 font-semibold hover:underline">
            <ArrowLeft size={14} /> Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  const title = [car.manufacture_year, car.make, car.model].filter(Boolean).join(' ');
  const photos = (car.photos && car.photos.length) ? car.photos : (car.cover_photo ? [car.cover_photo] : []);

  const dialMsg = `Hi, I'm enquiring about your ${title}${car.variant ? ' ' + car.variant : ''} listed on Unisol Global.`;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-marketplace">
          <ArrowLeft size={14} /> Back to listings
        </Link>

        {/* ===== Gallery + Summary header ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Gallery */}
          <div className="lg:col-span-3">
            <div className="relative bg-slate-100 rounded-2xl overflow-hidden aspect-[4/3]">
              {photos.length > 0 ? (
                <>
                  <img
                    src={photos[activePhoto]}
                    alt={title}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => setLightbox(true)}
                    data-testid="car-main-photo"
                  />
                  {photos.length > 1 && (
                    <>
                      <button onClick={() => setActivePhoto((i) => (i - 1 + photos.length) % photos.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md" aria-label="Previous photo">
                        <ChevronLeft size={20} />
                      </button>
                      <button onClick={() => setActivePhoto((i) => (i + 1) % photos.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md" aria-label="Next photo">
                        <ChevronRight size={20} />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">No photos yet</div>
              )}
              {car.listing_type === 'unisol_inventory' && (
                <span className="absolute top-3 left-3 bg-teal-500 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1">
                  <BadgeCheck size={12} /> Unisol Inventory
                </span>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {photos.map((p, idx) => (
                  <button
                    key={`th-${idx}`}
                    onClick={() => setActivePhoto(idx)}
                    className={`shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition ${
                      activePhoto === idx ? 'border-teal-500' : 'border-transparent hover:border-slate-300'
                    }`}
                    data-testid={`car-thumb-${idx}`}
                  >
                    {/* thumbnail */}
                    <img src={p} alt={`view ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sticky info card */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <p className="text-xs text-teal-700 font-semibold uppercase tracking-wider">{car.variant || 'Pre-owned car'}</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 leading-tight">{title}</h1>
              <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Calendar size={12} /> {car.manufacture_year}</span>
                <span className="inline-flex items-center gap-1"><Gauge size={12} /> {km(car.km_driven)}</span>
                <span className="inline-flex items-center gap-1"><Fuel size={12} /> {FUEL_LABEL[car.fuel_type] || car.fuel_type}</span>
              </div>

              <div className="mt-5 py-4 border-t border-slate-200">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Asking price</p>
                <p className="text-3xl font-bold text-slate-900 mt-0.5">{inr(car.asking_price)}</p>
              </div>

              {/* Seller card */}
              <div className="mt-2 bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-[11px] text-slate-400 uppercase tracking-wider">Listed by</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">{car.seller_name}</p>
                {car.city && (
                  <p className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                    <MapPin size={12} /> {car.city}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <a
                    href={`tel:${(car.seller_phone || '').replace(/[^\d+]/g, '')}`}
                    className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 text-sm"
                    data-testid="call-seller-btn"
                  >
                    <Phone size={15} /> Call
                  </a>
                  <a
                    href={waLink(car.seller_phone, dialMsg)}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 text-sm"
                    data-testid="wa-seller-btn"
                  >
                    <MessageCircle size={15} /> WhatsApp
                  </a>
                </div>
                <p className="text-[11px] text-slate-400 mt-3 text-center">
                  Mention you saw this on Unisol Global.
                </p>
              </div>

              {/* Secondary CTAs */}
              <div className="mt-5 space-y-2">
                {car.inspection_report?.share_token && (
                  <Link
                    to={`/inspection/${car.inspection_report.share_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center justify-between gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-4 rounded-lg transition group"
                    data-testid="view-inspection-report-btn"
                  >
                    <span className="flex items-center gap-2">
                      <FileCheck2 size={16} />
                      <span className="text-sm">View full inspection report</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-[11px] bg-white/15 px-2 py-1 rounded-full">
                      <Award size={11} />
                      {car.inspection_report.overall_grade || 'Report'}
                      {typeof car.inspection_report.overall_score === 'number' && (
                        <span className="opacity-80">
                          · {car.inspection_report.overall_score.toFixed(0)}
                        </span>
                      )}
                    </span>
                  </Link>
                )}
                <SecondaryCTA label="Enquire about Financing" onClick={() => {
                  trackFinanceEvent({ car_id: car.id, event_type: 'finance_lead_opened' });
                  setLeadModal('finance');
                }} />
                <SecondaryCTA label="Enquire about Insurance" onClick={() => setLeadModal('insurance')} />
                <SecondaryCTA label="Trade in my old car" onClick={() => setLeadModal('trade_in')} />
              </div>
            </div>

            {/* EMI calculator — collapses beneath the sticky info card on mobile */}
            <EmiCalculator
              carId={car.id}
              askingPrice={car.asking_price}
              onApply={() => {
                trackFinanceEvent({ car_id: car.id, event_type: 'finance_lead_opened', payload: { source: 'emi_widget' } });
                setLeadModal('finance');
              }}
            />
          </div>
        </div>

        {/* ===== Specs ===== */}
        <section className="mt-12">
          <h2 className="text-base font-semibold text-slate-600 uppercase tracking-wider mb-4">Key specifications</h2>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
            <Spec icon={Calendar} label="Year" value={car.manufacture_year} />
            <Spec icon={Gauge} label="Driven" value={km(car.km_driven)} />
            <Spec icon={Fuel} label="Fuel" value={FUEL_LABEL[car.fuel_type] || car.fuel_type} />
            <Spec icon={Cog} label="Transmission" value={TRANS_LABEL[car.transmission] || car.transmission} />
            {car.body_type && <Spec icon={ShieldCheck} label="Body" value={BODY_LABEL[car.body_type] || car.body_type} />}
            {car.color && <Spec icon={Palette} label="Colour" value={car.color} />}
            <Spec icon={Users} label="Owners" value={car.owners} />
            {car.registration_state && <Spec icon={MapPin} label="RTO" value={car.registration_state} />}
          </div>
        </section>

        {/* ===== Highlights & description ===== */}
        {(car.highlights?.length || car.description) && (
          <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {car.highlights?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="text-base font-semibold text-slate-600 uppercase tracking-wider mb-4">Highlights</h2>
                <ul className="space-y-2">
                  {car.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                      <BadgeCheck size={16} className="text-teal-600 shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {car.description && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="text-base font-semibold text-slate-600 uppercase tracking-wider mb-4">About this car</h2>
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{car.description}</p>
              </div>
            )}
          </section>
        )}

        {/* ===== Reassurance strip ===== */}
        <section className="mt-12 bg-slate-900 text-white rounded-2xl p-8">
          <h2 className="text-xl font-semibold">Why buy through Unisol</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5 text-sm text-slate-300">
            <p>✓ Every car is physically inspected by our detailing team before listing.</p>
            <p>✓ Optional PPF / ceramic coating at handover — at studio rates.</p>
            <p>✓ Financing &amp; insurance connections — no platform fees, ever.</p>
          </div>
        </section>
      </div>

      {/* ===== Lightbox ===== */}
      {lightbox && photos.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="car-lightbox">
          <button className="absolute top-5 right-5 text-white" onClick={() => setLightbox(false)} aria-label="Close">
            <X size={28} />
          </button>
          <img src={photos[activePhoto]} alt="full size" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* ===== Lead modal ===== */}
      {leadModal && (
        <LeadModal
          kind={leadModal}
          car={car}
          onClose={() => setLeadModal(null)}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────── reusable sub-components ──
const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon size={18} className="text-teal-600 mt-0.5 shrink-0" />
    <div>
      <p className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-slate-900 mt-0.5">{value || '—'}</p>
    </div>
  </div>
);

const SecondaryCTA = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="w-full bg-white border border-slate-300 hover:border-teal-500 hover:text-teal-700 text-slate-700 font-medium py-2.5 rounded-lg text-sm flex items-center justify-between px-4 transition"
    data-testid={`cta-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {label} <span className="text-slate-400">›</span>
  </button>
);

const LeadModal = ({ kind, car, onClose }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const titles = {
    finance:   { h: 'Financing enquiry',  sub: 'Tell us your details and our team will share EMI options.' },
    insurance: { h: 'Insurance enquiry',  sub: 'We work with leading insurers and will share quotes shortly.' },
    trade_in:  { h: 'Trade in your car',  sub: "Share your old car details and we'll send a buyback offer." },
    general:   { h: 'Enquire',            sub: "We'll get back within a business day." },
  };
  const t = titles[kind] || titles.general;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/marketplace/leads`, {
        car_id: car?.id || null,
        kind,
        ...form,
      });
      setSent(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not submit — please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="lead-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 sm:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-xl font-bold text-slate-900">{t.h}</h3>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-500">{t.sub}</p>

        {sent ? (
          <div className="mt-6 bg-teal-50 border border-teal-200 text-teal-800 rounded-xl p-4 text-sm" data-testid="lead-sent">
            ✓ Got it. Our team will reach out to <b>{form.phone}</b> shortly.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name *"
              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400"
              data-testid="lead-name" />
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone *" inputMode="tel"
              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400"
              data-testid="lead-phone" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email (optional)" type="email"
              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400"
              data-testid="lead-email" />
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={
                kind === 'trade_in'
                  ? 'Your old car details (make / model / year / KM)…'
                  : kind === 'finance'
                  ? 'Loan amount needed, tenure, anything else…'
                  : 'Any details that help us help you…'
              }
              rows={3}
              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400 resize-none"
              data-testid="lead-message" />
            <button type="submit" disabled={sending}
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-3 rounded-lg text-sm"
              data-testid="lead-submit">
              {sending ? 'Submitting…' : 'Submit enquiry'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default MarketplaceCar;
